/**
 * API client for scan operations.
 * All endpoints (except direct blob upload) require auth.
 */

import type { Scan, CreateScanRequest, UploadUrlsResponse, ScanListResponse } from '@app-types/scan'
import { apiUrl } from '../config'
import { getAuthHeaders } from '../stores/authStore'

const API_BASE = apiUrl('/api')

export async function createScan(req: CreateScanRequest): Promise<Scan> {
  const body: Record<string, unknown> = {
    shop_id: req.shop_id,
    section_id: req.section_id,
    section_name: req.section_name,
    frame_count: req.frame_count,
    location: req.location,
  }
  if (req.section_category) body.section_category = req.section_category
  // Rescan fields — only include when set
  if (req.mode) body.mode = req.mode
  if (req.anchor_frame_index != null) body.anchor_frame_index = req.anchor_frame_index

  const res = await fetch(`${API_BASE}/scans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const detail = body?.detail || res.statusText
    throw new Error(`Create scan failed: ${res.status} — ${detail}`)
  }
  return res.json()
}

export async function getUploadUrls(scanId: string, frameCount: number, includeUltra: boolean = false): Promise<UploadUrlsResponse> {
  const res = await fetch(`${API_BASE}/scans/${scanId}/upload-urls`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ frame_count: frameCount, include_ultra: includeUltra }),
  })
  if (!res.ok) throw new Error(`Get upload URLs failed: ${res.status}`)
  return res.json()
}

/**
 * Write the upload-complete marker. Call this ONLY after every frame has uploaded.
 *
 * It lands in the same folder as the frames, and its presence is what tells the scene
 * automation (services/api/tools/auto_3d.py) that this section is finished and safe to
 * reconstruct. Until it exists, a sweep leaves the section alone -- a section is registered when
 * the upload STARTS, so without this the automation could pick up a scan still arriving and
 * build a scene from half of it.
 */
export async function markUploadComplete(markerUrl: string, frameCount: number): Promise<void> {
  const body = JSON.stringify({
    status: 'done',
    frameCount,
    completedAt: new Date().toISOString(),
  })
  const res = await fetch(markerUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/json' },
    body,
  })
  if (!res.ok) throw new Error(`Marker upload failed: ${res.status}`)
}

export async function uploadFrame(uploadUrl: string, blob: Blob, maxRetries: number = 5): Promise<void> {
  // Direct to Azure Blob with SAS URL — no auth header needed
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'image/jpeg',
        },
        body: blob,
      })
      if (!res.ok) throw new Error(`Upload frame failed: ${res.status}`)
      return // Success
    } catch (err) {
      if (attempt === maxRetries) throw err
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s (capped at 30s)
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 30000)
      await new Promise(r => setTimeout(r, delay))
    }
  }
}

export async function markScanComplete(scanId: string): Promise<Scan> {
  const res = await fetch(`${API_BASE}/scans/${scanId}/complete`, {
    method: 'POST',
    headers: { ...getAuthHeaders() },
  })
  if (!res.ok) throw new Error(`Mark complete failed: ${res.status}`)
  return res.json()
}

export async function getScan(scanId: string): Promise<Scan> {
  const res = await fetch(`${API_BASE}/scans/${scanId}`, {
    headers: { ...getAuthHeaders() },
  })
  if (!res.ok) throw new Error(`Get scan failed: ${res.status}`)
  return res.json()
}

export async function listScans(shopId?: string): Promise<ScanListResponse> {
  const params = shopId ? `?shopId=${shopId}` : ''
  const res = await fetch(`${API_BASE}/scans${params}`, {
    headers: { ...getAuthHeaders() },
  })
  if (!res.ok) throw new Error(`List scans failed: ${res.status}`)
  return res.json()
}
